import type { ReactNode } from 'react';
import {
  AccountBalanceWalletOutlined,
  CalendarMonthOutlined,
  DescriptionOutlined,
  DevicesOutlined,
  GavelOutlined,
  GroupsOutlined,
  SecurityOutlined,
  ShoppingCartOutlined,
  VpnKeyOutlined,
} from '@mui/icons-material';
import type { WorkflowTemplateIconKey } from './model';
import { designTokens } from '../../design-system';

export type WorkflowTemplateIconOption = {
  key: WorkflowTemplateIconKey;
  label: string;
  icon: ReactNode;
  surface: string;
  color: string;
};

export const workflowTemplateIconOptions: readonly WorkflowTemplateIconOption[] = [
  { key: 'DOCUMENT', label: '문서', icon: <DescriptionOutlined />, surface: designTokens.color.subtleSurface, color: designTokens.color.secondaryText },
  { key: 'WALLET', label: '비용', icon: <AccountBalanceWalletOutlined />, surface: designTokens.color.successSoft, color: designTokens.color.successDark },
  { key: 'CART', label: '구매', icon: <ShoppingCartOutlined />, surface: designTokens.color.warningSoft, color: designTokens.color.warningDark },
  { key: 'SHIELD', label: '보안', icon: <SecurityOutlined />, surface: designTokens.color.actionSoft, color: designTokens.color.action },
  { key: 'CALENDAR', label: '일정', icon: <CalendarMonthOutlined />, surface: designTokens.color.infoSoft, color: designTokens.color.infoDark },
  { key: 'CONTRACT', label: '계약', icon: <GavelOutlined />, surface: designTokens.color.warningSoft, color: designTokens.color.warningDark },
  { key: 'ACCESS', label: '권한', icon: <VpnKeyOutlined />, surface: designTokens.color.actionSoft, color: designTokens.color.action },
  { key: 'EQUIPMENT', label: '장비', icon: <DevicesOutlined />, surface: designTokens.color.infoSoft, color: designTokens.color.infoDark },
  { key: 'PEOPLE', label: '구성원', icon: <GroupsOutlined />, surface: designTokens.color.successSoft, color: designTokens.color.successDark },
] as const;

const defaultWorkflowTemplateIcon = workflowTemplateIconOptions[0];
const workflowTemplateIconByKey = new Map<WorkflowTemplateIconKey, WorkflowTemplateIconOption>(
  workflowTemplateIconOptions.map((option) => [option.key, option]),
);

export function isWorkflowTemplateIconKey(value: unknown): value is WorkflowTemplateIconKey {
  return typeof value === 'string' && workflowTemplateIconByKey.has(value as WorkflowTemplateIconKey);
}

export function workflowTemplateIconVisual(iconKey?: WorkflowTemplateIconKey): WorkflowTemplateIconOption {
  return iconKey ? workflowTemplateIconByKey.get(iconKey) ?? defaultWorkflowTemplateIcon : defaultWorkflowTemplateIcon;
}

export function inferWorkflowTemplateIconKey(code: string, category?: string): WorkflowTemplateIconKey {
  const signal = `${code} ${category?.trim() || '기타'}`.toLocaleUpperCase('ko');
  if (signal.includes('EQUIPMENT') || signal.includes('ASSET') || signal.includes('DEVICE') || signal.includes('장비')) return 'EQUIPMENT';
  if (signal.includes('SECURITY') || signal.includes('보안')) return 'SHIELD';
  if (signal.includes('LEAVE') || signal.includes('ABSENCE') || signal.includes('휴가')) return 'CALENDAR';
  if (signal.includes('PURCHASE') || signal.includes('구매')) return 'CART';
  if (signal.includes('EXPENSE') || signal.includes('FINANCE') || signal.includes('재무') || signal.includes('비용')) return 'WALLET';
  if (signal.includes('CONTRACT') || signal.includes('LEGAL') || signal.includes('계약') || signal.includes('법무')) return 'CONTRACT';
  if (signal.includes('ACCESS') || signal.includes('PERMISSION') || signal.includes('AUTH') || signal.includes('권한') || /(^| )IT($| )/.test(signal)) return 'ACCESS';
  if (signal.includes('PEOPLE') || signal.includes('PERSON') || signal.includes('TEAM') || signal.includes('인사') || signal.includes('조직')) return 'PEOPLE';
  return 'DOCUMENT';
}

export function resolveWorkflowTemplateIconKey(template: { iconKey?: unknown; code: string; category?: string }): WorkflowTemplateIconKey {
  return isWorkflowTemplateIconKey(template.iconKey)
    ? template.iconKey
    : inferWorkflowTemplateIconKey(template.code, template.category);
}

export function resolveWorkflowTemplateIconVisual(template: { iconKey?: unknown; code: string; category?: string }): WorkflowTemplateIconOption {
  return workflowTemplateIconVisual(resolveWorkflowTemplateIconKey(template));
}
